import { Minus, Plus } from "lucide-react";

import { MISSION_INFO } from "@/lib/constants";
import { ChipGroup, Field, SectionLabel, SelectField } from "@/components/ui";

export const emptyPrefs = {
  genders: [], min_age: 25, max_age: 40, countries: [], residence_countries: [], missions: [], occupations: [],
  income_ranges: [], marital_statuses: [], educations: [], religions: [], languages: [], willing_to_relocate: null,
};

export function AgeStepper({ label, value, onChange, testId }) {
  const btn = "flex h-11 w-11 items-center justify-center rounded-full bg-[#F6E3E4] text-primary transition-colors hover:bg-[#efd3d5]";
  return (
    <div className="flex-1 rounded-xl border border-border bg-white p-3">
      <p className="mb-2 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center justify-between">
        <button type="button" aria-label={`Decrease ${label}`} data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(18, value - 1))} className={btn}><Minus className="h-5 w-5" /></button>
        <span className="font-display text-[28px] font-bold" data-testid={`${testId}-value`}>{value}</span>
        <button type="button" aria-label={`Increase ${label}`} data-testid={`${testId}-plus`} onClick={() => onChange(Math.min(100, value + 1))} className={btn}><Plus className="h-5 w-5" /></button>
      </div>
    </div>
  );
}

const summary = (v) => (v.length ? v.join(", ") : "No preference");
const Gap = () => <div className="h-5" />;

export function StepPreferences({ value, onChange, interests, onInterests, options, error, openSelect }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const labels = options.missions.map((m) => MISSION_INFO[m].label);
  return (
    <div data-testid="wizard-step-preferences">
      <h1 className="text-[34px] leading-tight">Your preferences</h1>
      <p className="mb-6 mt-1 text-sm text-muted-foreground">Choose “No preference” for anything that doesn’t matter to you.</p>
      <SectionLabel>Interested in</SectionLabel>
      <ChipGroup testIdPrefix="pref-genders" noPreference options={options.genders} value={value.genders} onChange={set("genders")} />
      <Gap />
      <SectionLabel>Age range</SectionLabel>
      <div className="flex gap-3">
        <AgeStepper testId="pref-min-age" label="Minimum" value={value.min_age ?? 25} onChange={set("min_age")} />
        <AgeStepper testId="pref-max-age" label="Maximum" value={value.max_age ?? 40} onChange={set("max_age")} />
      </div>
      {error ? <p className="mt-1.5 text-xs font-medium text-[#9E3C3E]">{error}</p> : null}
      <Gap />
      <div className="grid gap-x-4 sm:grid-cols-2">
        <SelectField testId="pref-countries-select" label="Preferred nationalities" value={summary(value.countries)} placeholder="No preference" onClick={() => openSelect("countries")} />
        <SelectField testId="pref-residence-select" label="Preferred countries of residence" value={summary(value.residence_countries)} placeholder="No preference" onClick={() => openSelect("residence_countries")} />
      </div>
      <SectionLabel>Preferred missions</SectionLabel>
      <ChipGroup testIdPrefix="pref-missions" noPreference options={labels} value={value.missions.map((m) => MISSION_INFO[m].label)}
        onChange={(l) => set("missions")(options.missions.filter((m) => l.includes(MISSION_INFO[m].label)))} />
      <p className="mt-2 text-xs text-muted-foreground">No preference shows all missions, with yours prioritised.</p>
      <Gap />
      <SectionLabel>Your interests & hobbies</SectionLabel>
      <ChipGroup testIdPrefix="pref-interests" options={options.interests} value={interests} onChange={onInterests} />
      <Gap />
      <Field testId="pref-occupations-input" label="Preferred occupations (optional)" value={value.occupations.join(", ")} placeholder="e.g. Doctor, Engineer"
        hint="Separate with commas. Leave empty for no preference." onChange={(e) => set("occupations")(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
      <SectionLabel>Marital status</SectionLabel>
      <ChipGroup testIdPrefix="pref-marital" noPreference options={options.marital_statuses} value={value.marital_statuses} onChange={set("marital_statuses")} />
      <Gap />
      <SectionLabel note="Optional">Education</SectionLabel>
      <ChipGroup testIdPrefix="pref-education" noPreference options={options.educations} value={value.educations} onChange={set("educations")} />
      <Gap />
      <SectionLabel note="Optional">Religion</SectionLabel>
      <ChipGroup testIdPrefix="pref-religion" noPreference options={options.religions.filter((r) => r !== "Prefer not to say")} value={value.religions} onChange={set("religions")} />
      <Gap />
      <SectionLabel>Language</SectionLabel>
      <ChipGroup testIdPrefix="pref-language" noPreference options={options.languages} value={value.languages} onChange={set("languages")} />
      <Gap />
      <SectionLabel note="Optional · private">Income range</SectionLabel>
      <ChipGroup testIdPrefix="pref-income" noPreference options={options.income_ranges.filter((r) => r !== "Prefer not to say")} value={value.income_ranges} onChange={set("income_ranges")} />
      <Gap />
      <SectionLabel>Willing to relocate?</SectionLabel>
      <ChipGroup testIdPrefix="pref-relocate" multi={false} options={options.relocate} value={value.willing_to_relocate ? [value.willing_to_relocate] : []} onChange={(v) => set("willing_to_relocate")(v[0] ?? null)} />
    </div>
  );
}
